<template>
  <v-dialog
    :value="open"
    max-width="400"
    content-class="cprv-dialog"
    @click:outside="onClose"
    @keydown.esc="onClose"
  >
    <div class="cprv" role="dialog" aria-labelledby="cprv-title">
      <header class="cprv__header">
        <div class="cprv__icon" aria-hidden="true">
          <img :src="editIcon" alt="" width="24" height="24" />
        </div>
        <button
          type="button"
          class="cprv__close"
          aria-label="Close"
          :disabled="saving"
          @click="onClose"
        >
          <img
            :src="require('@components/assets/x-close.svg')"
            alt=""
            width="24"
            height="24"
          />
        </button>
        <div class="cprv__intro">
          <h2 id="cprv-title" class="cprv__title">Correct this version</h2>
          <p class="cprv__copy">
            Update this version's start date, end date, min, and max. Saving
            updates this version in place and marks it Corrected.
          </p>
        </div>
      </header>

      <p class="cprv__summary">{{ summary }}</p>

      <div class="cprv__field">
        <label class="cprv__label">Start date</label>
        <div class="cprv__picker">
          <DatePicker
            :value="startDate"
            :minDate="bounds.startMin"
            :maxDate="bounds.startMax"
            :modal="true"
            :customCalendarIcon="true"
            dataPickerPlaceHolder="Select date"
            @onApplyDate="onStartApply"
          />
        </div>
        <p v-if="startConstraint" class="cprv__hint">{{ startConstraint }}</p>
      </div>

      <div class="cprv__field">
        <label class="cprv__label">End date</label>
        <div class="cprv__picker">
          <DatePicker
            :value="endDate"
            :minDate="bounds.endMin"
            :maxDate="bounds.endMax"
            :modal="true"
            :customCalendarIcon="true"
            dataPickerPlaceHolder="Select date"
            @onApplyDate="onEndApply"
          />
        </div>
        <p v-if="endConstraint" class="cprv__hint">{{ endConstraint }}</p>
      </div>

      <div class="cprv__amounts">
        <div class="cprv__field cprv__field--half">
          <label class="cprv__label" for="cprv-min">Min ({{ basisLabel }})</label>
          <div class="cprv__amount">
            <input
              id="cprv-min"
              class="cprv__amount-input"
              type="text"
              inputmode="numeric"
              :value="minDisplay"
              :disabled="saving"
              @input="onMinInput"
            />
          </div>
        </div>
        <div class="cprv__field cprv__field--half">
          <label class="cprv__label" for="cprv-max">Max ({{ basisLabel }})</label>
          <div class="cprv__amount">
            <input
              id="cprv-max"
              class="cprv__amount-input"
              type="text"
              inputmode="numeric"
              :value="maxDisplay"
              :disabled="saving"
              @input="onMaxInput"
            />
          </div>
        </div>
      </div>

      <div v-if="warning" class="cprv__callout">
        <p class="cprv__callout-title">{{ warning.title }}</p>
        <p class="cprv__callout-body">{{ warning.body }}</p>
      </div>

      <footer class="cprv__footer">
        <button
          type="button"
          class="cprv__btn cprv__btn--secondary"
          :disabled="saving"
          @click="onClose"
        >
          Cancel
        </button>
        <button
          type="button"
          class="cprv__btn cprv__btn--primary"
          :disabled="!canConfirm || saving"
          @click="onConfirm"
        >
          {{ saving ? "Correcting…" : "Correct" }}
        </button>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import { toApiDateString } from "@components/PayRangeDetailPage/composables/usePayRangeDetailPageState";
import {
  amountBasisLabel,
  buildAdjacentWarning,
  constraintCopy,
  dateBounds,
  findAdjacentVersions,
  formatAmountInput,
  formatVersionSummary,
  parseAmountInput,
} from "./correctPayRangeVersionUtils";
import editIcon from "./edit-05.svg";

const props = defineProps({
  open: { type: Boolean, default: false },
  version: { type: Object, default: null },
  versions: { type: Array, default: () => [] },
  unitSuffix: { type: String, default: "USD/year" },
  saving: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "confirm"]);

const startDate = ref("");
const endDate = ref("");
const minAmount = ref(null);
const maxAmount = ref(null);

watch(
  () => [props.open, props.version],
  () => {
    if (!props.open || !props.version) return;
    startDate.value = toApiDateString(props.version.startDate) || "";
    endDate.value = toApiDateString(props.version.endDate) || "";
    minAmount.value = parseAmountInput(props.version.minAmount);
    maxAmount.value = parseAmountInput(props.version.maxAmount);
  },
  { immediate: true }
);

const adjacent = computed(() => findAdjacentVersions(props.versions, props.version));

const bounds = computed(() =>
  dateBounds({
    previous: adjacent.value.previous,
    next: adjacent.value.next,
    startDate: startDate.value,
    endDate: endDate.value,
  })
);

const startConstraint = computed(() =>
  constraintCopy(bounds.value.startMin, bounds.value.startMax)
);
const endConstraint = computed(() =>
  constraintCopy(bounds.value.endMin, bounds.value.endMax)
);

const warning = computed(() =>
  buildAdjacentWarning({
    previous: adjacent.value.previous,
    next: adjacent.value.next,
    startDate: startDate.value,
    endDate: endDate.value,
  })
);

const summary = computed(() =>
  props.version ? formatVersionSummary(props.version) : ""
);
const basisLabel = computed(() => amountBasisLabel(props.unitSuffix));
const minDisplay = computed(() =>
  minAmount.value == null ? "" : `$${formatAmountInput(minAmount.value)}`
);
const maxDisplay = computed(() =>
  maxAmount.value == null ? "" : `$${formatAmountInput(maxAmount.value)}`
);

const canConfirm = computed(() => {
  if (!startDate.value || minAmount.value == null || maxAmount.value == null) return false;
  if (maxAmount.value <= minAmount.value) return false;
  if (endDate.value && endDate.value <= startDate.value) return false;
  return true;
});

function onStartApply(date) {
  startDate.value = toApiDateString(date) || "";
}

function onEndApply(date) {
  endDate.value = toApiDateString(date) || "";
}

function onMinInput(event) {
  minAmount.value = parseAmountInput(event.target.value);
}

function onMaxInput(event) {
  maxAmount.value = parseAmountInput(event.target.value);
}

function onClose() {
  if (props.saving) return;
  emit("close");
}

function onConfirm() {
  if (!canConfirm.value || props.saving) return;
  emit("confirm", {
    versionId: props.version?.versionId ?? props.version?.id ?? null,
    versionNumber: props.version?.versionNumber,
    startDate: startDate.value,
    endDate: endDate.value || null,
    minAmount: minAmount.value,
    maxAmount: maxAmount.value,
  });
}
</script>

<style scoped>
.cprv {
  background: #fff;
  border-radius: 12px;
  box-shadow:
    0 20px 24px -4px rgba(16, 24, 40, 0.08),
    0 8px 8px -4px rgba(16, 24, 40, 0.03);
  overflow: hidden;
  width: 100%;
}

.cprv__header {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 24px 0;
}

.cprv__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.cprv__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
}

.cprv__close:hover {
  background: #f9fafb;
}

.cprv__close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cprv__intro {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.cprv__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.cprv__copy {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.cprv__summary {
  margin: 0;
  padding: 4px 24px 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  text-align: center;
}

.cprv__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 24px;
  margin-top: 12px;
}

.cprv__field--half {
  flex: 1 1 0;
  min-width: 0;
  padding: 0;
  margin-top: 0;
}

.cprv__label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.cprv__hint {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #667086;
}

.cprv__picker {
  width: 100%;
}

.cprv__picker ::v-deep .date-picker-display {
  width: auto;
  justify-content: flex-start;
  gap: 4px;
  min-height: 40px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  padding: 10px 14px;
  background: #fff;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.cprv__amounts {
  display: flex;
  gap: 20px;
  padding: 12px 24px;
}

.cprv__amount {
  width: 100%;
}

.cprv__amount-input {
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.cprv__amount-input:focus {
  outline: none;
  border-color: #7f56d9;
}

.cprv__callout {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 12px 14px;
  background: #fffcf5;
  border: 1px solid #fde3a8;
  color: #b54708;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: normal;
}

.cprv__callout-title {
  margin: 0;
  font-weight: 600;
}

.cprv__callout-body {
  margin: 0;
  font-weight: 400;
}

.cprv__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 32px 24px 24px;
}

.cprv__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.cprv__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cprv__btn--secondary {
  flex: 1 1 0;
  background: #fff;
  border-color: #d0d5dd;
  color: #344054;
}

.cprv__btn--secondary:hover:not(:disabled) {
  background: #f9fafb;
}

.cprv__btn--primary {
  background: #7f56d9;
  border-color: #7f56d9;
  color: #fff;
}

.cprv__btn--primary:hover:not(:disabled) {
  background: #6941c6;
  border-color: #6941c6;
}
</style>

<style>
.cprv-dialog {
  box-shadow: none !important;
  background: transparent !important;
  border-radius: 12px !important;
  overflow: visible !important;
}
</style>
